import { useCallback, useEffect, useState } from 'react';
import {
  FieldValues,
  useFormContext,
  UseFormGetValues,
  useWatch,
} from 'react-hook-form';
import { Modal } from '../../common/components';
import { compareIsoDates, ISODate } from '../../common/utils';
import { difference } from '../../common/utils/set';
import { ExecutionCalendarFormDataType } from '../schema';
import { cloneYearDates } from './cloneYearDates';
import translations from '../translations/useCalendarSelect.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';

const { addTranslations, t } = i18nNsFactory('use-calendar-select');
addTranslations(translations);

export interface CalendarIndex {
  calendar: ExecutionCalendarFormDataType;
  index: number;
}

export function useCalendarSelect(fieldPath: string) {
  const { getValues, setValue } = useFormContext();
  const [selected, setSelected] = useState(() =>
    findSelected(fieldPath, getValues),
  );
  const count: number = useWatch({
    name: `${fieldPath}.length`,
  });

  const curbSelection = useCallback(
    (count: number) => {
      setSelected((orig) =>
        orig.size > count ? findSelected(fieldPath, getValues) : orig,
      );
    },
    [fieldPath, getValues, setSelected],
  );

  useEffect(() => {
    curbSelection(count);
  }, [curbSelection, count]);

  const cloneLastYear = useCallback(() => {
    const calendar = formCalendar(fieldPath, getValues);
    const { error, dates } = cloneYearDates(calendar);
    if (error) {
      Modal.error({
        title: t('modal.error.title'),
        content: t(error),
        onOk: () => Modal.destroyAll(),
      });
    } else if (dates.length) {
      setValue(fieldPath, [...getValues(fieldPath), ...dates], {
        shouldDirty: true,
      });
      setSelected(
        (state) =>
          new Set([...state, ...dates.map((date) => date.executionDate)]),
      );
    }
  }, [fieldPath, getValues, setValue]);

  const updateValues = useCallback(
    (selected: Set<ISODate>) => {
      const calendar = formCalendar(fieldPath, getValues);
      const existingDates: Map<number, CalendarIndex> = new Map(
        calendar.map((cal, index) => [
          new Date(cal.executionDate).valueOf(),
          { calendar: cal, index },
        ]),
      );

      // Remove dates that are not in the selected set and add dates that are not in the existing set
      const added: ISODate[] = [];
      const existing: number[] = [];

      selected.forEach((dateStr) => {
        const unixTs = new Date(dateStr).valueOf();
        const index = existingDates.get(unixTs);
        if (index) {
          existing.push(index.index);
        } else {
          added.push(dateStr);
        }
      });
      const deleted = [...difference(Array(calendar.length).keys(), existing)];
      deleted.reverse();

      const newCalendar = [...calendar];
      deleted.forEach((i) => newCalendar.splice(i, 1));
      added.forEach((executionDate) => {
        newCalendar.push({
          id: null,
          executionId: null,
          executionDate,
          validPeriod: [null, null],
          sourceDataVersionId: getValues('execution.sourceDataVersionId'),
        });
      });

      newCalendar.sort((a, b) =>
        compareIsoDates(b.executionDate, a.executionDate),
      );
      setValue(fieldPath, newCalendar, {
        shouldDirty: true,
      });
    },
    [fieldPath, getValues, setValue],
  );

  return { cloneLastYear, selected, setSelected, updateValues };
}

function findSelected(
  fieldPath: string,
  getValues: UseFormGetValues<FieldValues>,
) {
  const calendar = formCalendar(fieldPath, getValues);
  return new Set(calendar.map((date) => date.executionDate));
}

function formCalendar(
  fieldPath: string,
  getValues: UseFormGetValues<FieldValues>,
) {
  return (getValues(fieldPath) ?? []) as ExecutionCalendarFormDataType[];
}
